import {
  GetShipmentsRequest,
  ShipmentDetailsResponse,
  UpdateShipmentDetailsByIdRequest,
  getShipmentById,
  getShipmentItemApi,
  getShipmentItemsApi,
  getShipmentMovementScheduleById,
  getShipments,
  updateShipmentDetailsById
} from '../api';
import { useAuthStore } from '@/features/Auth/model/authStore';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const shipmentsQueryKeys = {
  all: ['shipments'] as const,
  detail: (id: string) => [...shipmentsQueryKeys.all, 'detail', id] as const,
  item: (shipmentId: string, itemId: string) =>
    [...shipmentsQueryKeys.all, 'items', shipmentId, itemId] as const,
  items: (shipmentId: string) =>
    [...shipmentsQueryKeys.all, 'items', shipmentId] as const,
  list: (query: GetShipmentsRequest) => [...shipmentsQueryKeys.all, 'list', query] as const,
  movementSchedule: (shipmentId: string) => [...shipmentsQueryKeys.all, 'movement-schedule', shipmentId] as const
};

interface UseShipmentDetailsApiOptions {
  enabled?: boolean;
  refetchInterval?: number | false;
}

/**
 * Pulls org_id from the persisted auth token data (the value the user sees
 * under `wl-auth → state.tokenData.org_id` in localStorage).
 */
export function useOrgId(): string | null {
  return useAuthStore((state) => state.tokenData?.org_id ?? null);
}

export function useShipmentsListApi(query: Omit<GetShipmentsRequest, 'org_id'> = {}) {
  const orgId = useOrgId();
  const fullQuery: GetShipmentsRequest = orgId ? { ...query, org_id: orgId } : query;

  return useQuery({
    enabled: Boolean(orgId),
    queryFn: () => getShipments(fullQuery),
    queryKey: shipmentsQueryKeys.list(fullQuery),
    staleTime: 30_000
  });
}

export function useShipmentDetailsApi(id: string | undefined, options: UseShipmentDetailsApiOptions = {}) {
  const isEnabled = options.enabled ?? true;

  return useQuery({
    enabled: Boolean(id) && isEnabled,
    queryFn: () => getShipmentById({ id: id as string }),
    queryKey: shipmentsQueryKeys.detail(id ?? ''),
    refetchInterval: options.refetchInterval,
    retry: false,
    staleTime: 30_000
  });
}

export function useShipmentItemsApi(shipmentId: string | undefined) {
  return useQuery({
    enabled: Boolean(shipmentId),
    queryFn: () => getShipmentItemsApi(shipmentId as string),
    queryKey: shipmentsQueryKeys.items(shipmentId ?? ''),
    staleTime: 30_000
  });
}

export function useShipmentMovementScheduleApi(shipmentId: string | undefined) {
  return useQuery({
    enabled: Boolean(shipmentId),
    queryFn: () => getShipmentMovementScheduleById({ id: shipmentId as string }),
    queryKey: shipmentsQueryKeys.movementSchedule(shipmentId ?? ''),
    staleTime: 30_000
  });
}

export function useShipmentItemApi(
  shipmentId: string | undefined,
  itemId: string | undefined
) {
  return useQuery({
    enabled: Boolean(shipmentId && itemId),
    queryFn: () => getShipmentItemApi(shipmentId as string, itemId as string),
    queryKey: shipmentsQueryKeys.item(shipmentId ?? '', itemId ?? ''),
    staleTime: 30_000
  });
}

export function useUpdateShipmentDetailsByIdApi(shipmentId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateShipmentDetailsByIdRequest) => updateShipmentDetailsById(payload),
    onSuccess: async (data) => {
      if (shipmentId) {
        if (
          data?.supplier_delivery_permit_refresh_pending !== undefined ||
          data?.supplier_delivery_permit_refresh_failed !== undefined
        ) {
          queryClient.setQueryData<ShipmentDetailsResponse>(
            shipmentsQueryKeys.detail(shipmentId),
            (previousData) => (
              previousData
                ? {
                    ...previousData,
                    supplier_delivery_permit_refresh_failed:
                      data.supplier_delivery_permit_refresh_failed ??
                      (data.supplier_delivery_permit_refresh_pending ? false : previousData.supplier_delivery_permit_refresh_failed),
                    supplier_delivery_permit_refresh_pending:
                      data.supplier_delivery_permit_refresh_pending ?? previousData.supplier_delivery_permit_refresh_pending
                  }
                : previousData
            )
          );
        }

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: shipmentsQueryKeys.detail(shipmentId)
          }),
          queryClient.invalidateQueries({
            queryKey: shipmentsQueryKeys.movementSchedule(shipmentId)
          }),
          queryClient.invalidateQueries({
            queryKey: ['getShipmentAttachmentsById']
          }),
          queryClient.refetchQueries({
            queryKey: ['getUserActions'],
            type: 'all'
          })
        ]);
      }
    }
  });
}